import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

// A server's name (`PUT /v1/runners/server-label`, #6566), the ONE form a group header's
// "Name this server" and the details panel's About "Server" edit both open. Presenter:
// `createServerNameWrite` (`lib/name-write.ts`) owns the write, resolves the machine id set
// off the unfiltered read (never the group's shown rows), trims what Save hands it and
// sends `null` for an empty field.
//
// AN EMPTY FIELD CLEARS, said on screen (`server_name_desc`), and toasts "Server name cleared."

export function ServerNameForm(props: {
  /** The label the server carries today, or null when it is unnamed. */
  readonly currentLabel: string | null;
  /** Distinct per mount, so the header's and the panel's forms never share an id. */
  readonly id: string;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSave: (text: string) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [text, setText] = createSignal(props.currentLabel ?? '');

  const submit = (e: Event): void => {
    e.preventDefault();
    if (props.pending) return;
    props.onSave(text());
  };

  return (
    <form class="field-group box-manage__form" onSubmit={submit}>
      <Field
        label={t('fleet.machines.server_name_label')}
        id={props.id}
        desc={t('fleet.machines.server_name_desc')}
      >
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="text"
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <div class="box-manage__actions">
        <button type="submit" class={buttonClass({ size: 'sm' })} disabled={props.pending}>
          {t(
            props.pending ? 'fleet.machines.server_name_saving' : 'fleet.machines.server_name_save',
          )}
        </button>
        <Button variant="ghost" size="sm" onClick={props.onCancel}>
          {t('fleet.machines.server_name_cancel')}
        </Button>
      </div>
    </form>
  );
}
